import { isSelectElement, VISUAL_ELEMENTS, isShowOutcome, isOptionsOutcome, } from './types.js';
import { evaluateOperator } from './operators.js';
const overrideMeta = (element, overrides) => {
    if (!overrides || !element.meta) {
        return element.meta;
    }
    if (typeof element.meta === 'object' && 'options' in element.meta && overrides.options) {
        return {
            ...element.meta,
            options: overrides.options,
        };
    }
};
const updateElementMeta = (element, overrides) => ({
    ...element,
    meta: overrideMeta(element, overrides?.metaOverrides),
    visible: overrides && overrides.visible !== undefined ? overrides.visible : true,
});
const toConditionalElementAnswer = (element, answer, overrides) => ({
    field: element.field ?? element.type,
    type: element.type,
    required: element.required ?? false,
    meta: overrideMeta(element, overrides?.metaOverrides),
    answer: answer ? answer.userInput : undefined,
    visible: overrides && overrides.visible !== undefined ? overrides.visible : true,
});
export const evaluateShowConditionalOutcome = (element, previousElementAnswers) => {
    const showOutcomes = element.conditions?.filter(isShowOutcome) || [];
    if (showOutcomes.length === 0) {
        return true;
    }
    if (showOutcomes.length > 1) {
        throw new Error(`Element ${element.id} has multiple show outcomes, which is not supported.`);
    }
    return evaluateOperator(showOutcomes[0].condition, previousElementAnswers);
};
export const evaluateOptionsConditionalOutcomes = (element, previousElementAnswers) => {
    const optionOutcomes = element.conditions?.filter(isOptionsOutcome) || [];
    if (optionOutcomes.length === 0) {
        return null;
    }
    if (!isSelectElement(element)) {
        throw new Error(`Options outcome is only supported on Select elements. Element ${element.id} is not a Select element.`);
    }
    const optionsOverridesValues = new Set(optionOutcomes.flatMap((outcome) => {
        if (evaluateOperator(outcome.condition, previousElementAnswers)) {
            return outcome.outcomeMeta.options;
        }
        return [];
    }));
    const optionsOverrides = element.meta.options.filter((option) => optionsOverridesValues.has(option.value));
    return optionsOverrides;
};
export const transformElementDefinitionBasedOnConditionality = (element, answers) => {
    if (!element.conditions || element.conditions.length === 0) {
        return updateElementMeta(element);
    }
    const isVisible = evaluateShowConditionalOutcome(element, answers);
    if (!isVisible) {
        return updateElementMeta(element, { visible: false });
    }
    const optionsOverrides = evaluateOptionsConditionalOutcomes(element, answers);
    if (optionsOverrides !== null) {
        if (optionsOverrides.length === 0) {
            return updateElementMeta(element, { visible: false });
        }
        return updateElementMeta(element, {
            metaOverrides: { options: optionsOverrides },
        });
    }
    return updateElementMeta(element);
};
export const transformElementDefinitionToElementAnswer = (element, answer, previousElementAnswers) => {
    const transformed = transformElementDefinitionBasedOnConditionality(element, previousElementAnswers);
    return toConditionalElementAnswer(transformed, answer, { visible: transformed.visible });
};
export const reconcileAnswersWithConditionallyTransformedElements = (elements, answers) => {
    const answerableElements = elements.filter((element) => !VISUAL_ELEMENTS.includes(element.type));
    const visibleElementAnswers = answerableElements.reduce((previousElementAnswers, element) => {
        const answer = answers.find((a) => a.elementId === element.id) || null;
        const transformedElement = transformElementDefinitionToElementAnswer(element, answer, previousElementAnswers);
        return transformedElement.visible
            ? [...previousElementAnswers, transformedElement]
            : previousElementAnswers;
    }, []);
    return visibleElementAnswers;
};
